import { useLocation } from "react-router-dom";

import { NavItemType } from "./Navbar";

const stripTrailingSlash = (path: string): string =>
  path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;

// A "home" href ("/", or a localized root like "/en" / "/fr") must match ONLY
// the exact path — otherwise its short prefix would light up on every page.
const isHomeHref = (href: string): boolean =>
  href === "/" || /^\/[a-z]{2}\/?$/.test(href);

const hrefMatches = (pathname: string, href: string): boolean => {
  const path = stripTrailingSlash(pathname);
  const target = stripTrailingSlash(href);
  if (isHomeHref(href)) {
    return path === target;
  }
  // Segment-aware prefix: `/app/blog` matches `/app/blog` and `/app/blog/42`,
  // but not `/app/blogs`.
  return path === target || path.startsWith(`${target}/`);
};

/**
 * Resolve which nav href is "active" for the current pathname, using a
 * longest-segment-prefix rule so EXACTLY ONE item lights up. On
 * `/app/blog/media` the `/app/blog/media` item wins over the shorter
 * `/app/blog` ("Articles") item; on `/app/blog/42/edit` (which has no dedicated
 * nav item) `/app/blog` is still the active one. This replaces NavLink's
 * per-link prefix match, which lights up every ancestor item at once.
 */
export const getActiveHref = (
  pathname: string,
  hrefs: readonly (string | undefined)[],
): string | undefined => {
  let best: string | undefined;
  for (const href of hrefs) {
    if (href === undefined || !hrefMatches(pathname, href)) {
      continue;
    }
    if (
      best === undefined ||
      stripTrailingSlash(href).length > stripTrailingSlash(best).length
    ) {
      best = href;
    }
  }
  return best;
};

/** Collect every href in a nav tree (including submenu children). */
const collectHrefs = (items: NavItemType[]): (string | undefined)[] =>
  items.flatMap((item) => [
    item.href,
    ...(item.submenu ? collectHrefs(item.submenu) : []),
  ]);

/**
 * Hook: the single active href for the current route across the whole nav tree.
 * Passed down to each rendered item so only the longest-matching one is marked
 * active.
 */
export const useActiveHref = (items: NavItemType[]): string | undefined => {
  const { pathname } = useLocation();
  return getActiveHref(pathname, collectHrefs(items));
};

export { stripTrailingSlash };
